import { useEffect, useState } from 'react';
import axios from '../../api/axiosInstance';
import { readStoredUser } from '../../utils/sessionUser';
import useCrossTabDataRefreshToken from '../../hooks/useCrossTabDataRefresh';
import useCatalogAccess from '../components/useCatalogAccess';

export const equivalentScopeMatches = (user) => {
  const current = readStoredUser();
  return String(current?.id) === String(user?.id) && String(current?.active_business_unit_id) === String(user?.active_business_unit_id);
};
export const equivalentRequestOptions = (user) => ({
  pilcoreExpectedUserId: user?.id,
  headers: { 'X-Pilcore-Business-Unit-Id': String(user?.active_business_unit_id) },
});
export default function useEquivalentProducts(path, permission, requestedUnit, enabled = true) {
  const [user, setUser] = useState(readStoredUser);
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState({ key: '', loading: false, data: null, error: false });
  const crossTab = useCrossTabDataRefreshToken();
  const unit = user?.active_business_unit_id;
  const report = permission === 'purchasing.reports.items_purchased.view';
  const access = useCatalogAccess(requestedUnit || unit);
  const canView = access.can(permission);
  const ready = Boolean(user?.id && unit && (!requestedUnit || String(requestedUnit) === String(unit)));
  const key = JSON.stringify([user?.id, unit, permission, canView, path, enabled, ready, access.contextKey]);
  useEffect(() => {
    const update = () => setUser(readStoredUser());
    window.addEventListener('palantir-session-updated', update); window.addEventListener('storage', update);
    return () => { window.removeEventListener('palantir-session-updated', update); window.removeEventListener('storage', update); };
  }, []);
  useEffect(() => {
    if (!canView || !ready || !enabled) return undefined;
    const controller = new AbortController();
    const stamp = access.capture();
    setState({ key, loading: true, data: null, error: false });
    axios.get(path, { ...access.requestConfig(permission), params: { business_unit_id: requestedUnit || unit, ...(!report ? { catalog: 1 } : {}) }, signal: controller.signal }).then(({ data }) => {
      if (controller.signal.aborted) return;
      if (!equivalentScopeMatches(user) || !access.isCurrent(stamp, permission)) { setUser(readStoredUser()); return; }
      if (!Array.isArray(data?.groups)) throw new Error('Invalid equivalents response');
      setState({ key, data, loading: false, error: false });
    }).catch(() => { if (!controller.signal.aborted && access.isCurrent(stamp, permission)) setState({ key, data: null, loading: false, error: true }); });
    return () => controller.abort();
    // The key contains every request scope and filter. User object changes alone must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, retry, crossTab]);
  const current = state.key === key && ready && canView && enabled;
  return { user, scope: key, canView, ready, data: current ? state.data : null,
    loading: canView && ready && enabled && (!current || state.loading), error: current && state.error,
    reload: () => setRetry((value) => value + 1) };
}
